/**
 * useChat Hook
 * Phase 6.5 - Chat Conversationnel (CFO Virtuel)
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { chatService } from '../services/chat.service';
import type {
  ConversationWithMessages,
  ChatWidgetState,
  SendMessageRequest,
} from '../types/chat.types';

const CHAT_QUERY_KEY = 'chat';

export function useChat() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const isAuthenticated = !!user;
  const [widgetState, setWidgetState] = useState<ChatWidgetState>({
    isOpen: false,
    isMinimized: false,
    isLoading: false,
  });

  // Track if we've initialized
  const initializedRef = useRef(false);

  /**
   * Get or create active conversation
   */
  const {
    data: activeConversation,
    isLoading: isLoadingConversation,
    refetch: refetchConversation,
  } = useQuery({
    queryKey: [CHAT_QUERY_KEY, 'active'],
    queryFn: () => chatService.getActiveConversation(),
    enabled: isAuthenticated && widgetState.isOpen,
    staleTime: 30000, // 30 seconds
  });

  /**
   * List conversations
   */
  const {
    data: conversationsData,
    isLoading: isLoadingConversations,
  } = useQuery({
    queryKey: [CHAT_QUERY_KEY, 'list'],
    queryFn: () => chatService.listConversations({ limit: 10 }),
    enabled: isAuthenticated && widgetState.isOpen,
    staleTime: 60000, // 1 minute
  });

  /**
   * Send message mutation
   */
  const sendMessageMutation = useMutation({
    mutationFn: (request: SendMessageRequest) => chatService.sendMessage(request),
    onMutate: () => {
      setWidgetState((prev) => ({ ...prev, isLoading: true, error: undefined }));
    },
    onSuccess: (response) => {
      if (response.success && response.data) {
        // Update active conversation in cache
        queryClient.setQueryData<ConversationWithMessages | null>(
          [CHAT_QUERY_KEY, 'active'],
          (old) => {
            if (!old) return old;
            return {
              ...old,
              messages: [
                ...old.messages,
                response.data!.userMessage,
                response.data!.assistantMessage,
              ],
              messageCount: old.messageCount + 2,
            };
          }
        );
      }
      setWidgetState((prev) => ({ ...prev, isLoading: false }));
    },
    onError: (error: Error) => {
      setWidgetState((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
    },
  });

  /**
   * Create new conversation mutation
   */
  const createConversationMutation = useMutation({
    mutationFn: () => chatService.createConversation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CHAT_QUERY_KEY] });
    },
  });

  /**
   * Close conversation mutation
   */
  const closeConversationMutation = useMutation({
    mutationFn: (id: string) => chatService.closeConversation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CHAT_QUERY_KEY] });
    },
  });

  /**
   * Submit feedback mutation
   */
  const submitFeedbackMutation = useMutation({
    mutationFn: ({ messageId, rating, comment }: { messageId: string; rating: number; comment?: string }) =>
      chatService.submitFeedback(messageId, { rating, comment }),
    onSuccess: (updatedMessage) => {
      // Update message in cache
      queryClient.setQueryData<ConversationWithMessages | null>(
        [CHAT_QUERY_KEY, 'active'],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.map((msg) =>
              msg.id === updatedMessage.id ? updatedMessage : msg
            ),
          };
        }
      );
    },
  });

  /**
   * Toggle widget open/closed
   */
  const toggleWidget = useCallback(() => {
    setWidgetState((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
      isMinimized: false,
    }));
  }, []);

  /**
   * Open widget
   */
  const openWidget = useCallback(() => {
    setWidgetState((prev) => ({
      ...prev,
      isOpen: true,
      isMinimized: false,
    }));
  }, []);

  /**
   * Close widget
   */
  const closeWidget = useCallback(() => {
    setWidgetState((prev) => ({
      ...prev,
      isOpen: false,
      isMinimized: false,
    }));
  }, []);

  /**
   * Minimize widget
   */
  const minimizeWidget = useCallback(() => {
    setWidgetState((prev) => ({
      ...prev,
      isMinimized: true,
    }));
  }, []);

  /**
   * Maximize widget
   */
  const maximizeWidget = useCallback(() => {
    setWidgetState((prev) => ({
      ...prev,
      isMinimized: false,
    }));
  }, []);

  /**
   * Send a message
   */
  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim()) return;

      const conversationId = activeConversation?.id;
      await sendMessageMutation.mutateAsync({
        content: content.trim(),
        conversationId,
      });
    },
    [activeConversation?.id, sendMessageMutation]
  );

  /**
   * Start a new conversation
   */
  const startNewConversation = useCallback(async () => {
    // Close current conversation if exists
    if (activeConversation?.id) {
      await closeConversationMutation.mutateAsync(activeConversation.id);
    }
    await createConversationMutation.mutateAsync();
  }, [activeConversation?.id, closeConversationMutation, createConversationMutation]);

  /**
   * Submit feedback for a message
   */
  const submitFeedback = useCallback(
    async (messageId: string, rating: number, comment?: string) => {
      await submitFeedbackMutation.mutateAsync({ messageId, rating, comment });
    },
    [submitFeedbackMutation]
  );

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setWidgetState((prev) => ({ ...prev, error: undefined }));
  }, []);

  // Update widget state with active conversation
  useEffect(() => {
    setWidgetState((prev) => ({
      ...prev,
      activeConversation: activeConversation || undefined,
    }));
  }, [activeConversation]);

  return {
    // State
    widgetState,
    activeConversation,
    conversations: conversationsData?.conversations || [],
    totalConversations: conversationsData?.total || 0,
    messages: activeConversation?.messages || [],

    // Loading states
    isLoading: widgetState.isLoading || isLoadingConversation,
    isLoadingConversations,
    isSending: sendMessageMutation.isPending,

    // Error
    error: widgetState.error,

    // Actions
    toggleWidget,
    openWidget,
    closeWidget,
    minimizeWidget,
    maximizeWidget,
    sendMessage,
    startNewConversation,
    submitFeedback,
    clearError,
    refetchConversation,
  };
}

export default useChat;
